import React, { useState, useEffect } from "react";
import { HiArrowSmLeft } from "react-icons/hi";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";

const SupplierAddCredit = ({ isOpen, onClose, onSave, supplierId, supplierReturnId }) => {
  const [formData, setFormData] = useState({
    credit_type: "Return Items",
    return_date: "",
    description: "",
    amount: "",
    selected_return_id: supplierReturnId || ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [returns, setReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);

  useEffect(() => {
    if (isOpen && supplierId) {
      fetchSupplierReturns();
    }
  }, [isOpen, supplierId]);

  const fetchSupplierReturns = async () => {
    setLoadingReturns(true);
    try {
      const response = await httpService.get(`/vendorsupplierreturn/getVendorSupplierReturns/${supplierId}`);
      if (response.status && response.code === 200) {
        const returnsData = response.data || [];
        setReturns(returnsData);
        
        if (supplierReturnId && returnsData.length > 0) {
          const matchingReturn = returnsData.find(r => r._id === supplierReturnId);
          if (matchingReturn) {
            handleReturnSelect(matchingReturn);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching supplier returns:", error);
      setError("Failed to load returns list");
    } finally {
      setLoadingReturns(false);
    }
  };

  const handleReturnSelect = (selectedReturn) => {
    if (!selectedReturn) {
      setFormData(prev => ({
        ...prev,
        selected_return_id: "",
        amount: "",
        return_date: ""
      }));
      return;
    }

    const returnAmount = selectedReturn.total_return_amount || 0;
    
    setFormData(prev => ({
      ...prev,
      selected_return_id: selectedReturn._id,
      amount: returnAmount.toString(),
      return_date: selectedReturn.createdAt ? new Date(selectedReturn.createdAt).toISOString().split('T')[0] : prev.return_date
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.selected_return_id) {
      setError("Please select a return");
      return;
    }
    
    if (!formData.amount || formData.amount.trim() === "") {
      setError("Please enter an amount");
      return;
    }
    
    if (!formData.credit_type || !formData.return_date) {
      setError("Please fill in all required fields");
      return;
    }

    if (!supplierId) {
      setError("Missing supplier ID. Please refresh and try again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData = {
        supplier_id: supplierId,
        credit_note_type: formData.credit_type === "Return Items" ? "return" : "other",
        credit_amount: parseFloat(formData.amount),
        credit_reason: formData.description || "Credit note added"
      };

      const returnId = formData.selected_return_id || supplierReturnId;
      if (returnId) {
        requestData.supplier_return_id = returnId;
      }

      const response = await httpService.post("/vendorsuppliercredit/addVendorSupplierCredit", requestData);
      
      // ✅ Fixed: Check for status true and code 200 OR 201
      if (response.status && (response.code === 200 || response.code === 201)) {
        // Show success toast with the message from backend
        toast.success(response.message || "Credit note added successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // Reset form
        setFormData({
          credit_type: "Return Items",
          return_date: "",
          description: "",
          amount: "",
          selected_return_id: ""
        });
        
        // Call onSave callback with the data
        if (onSave) {
          onSave(response.data);
        }
        
        // Close the drawer after a small delay to ensure toast is shown
        setTimeout(() => {
          onClose();
        }, 500);
        
      } else {
        setError(response.message || "Failed to add credit note");
        toast.error(response.message || "Failed to add credit note");
      }
    } catch (error) {
      console.error("Error adding credit note:", error);
      const errorMessage = error.response?.data?.message || "Failed to add credit note. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      credit_type: "Return Items",
      return_date: "",
      description: "",
      amount: "",
      selected_return_id: ""
    });
    setError(null);
    setReturns([]);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch {
      return "Invalid Date";
    }
  };

  return (
    <>
      <div className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]" onClick={handleClose} />
        <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center p-4 border-b bg-white shadow-sm gap-2">
            <button onClick={handleClose} className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
              <HiArrowSmLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-blue-900 ml-2">Add Credit Notes</h2>
          </div>
          {/* Content */}
          {isOpen && (
            <form onSubmit={handleSubmit} className="flex flex-col h-[calc(100vh-110px)] justify-between">
              <div className="px-6 pt-6 space-y-4 overflow-y-auto">
                {/* Dropdown for selecting return */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Return <span className="text-red-500">*</span>
                  </label>

                  <select
                    required
                    value={formData.selected_return_id}
                    onChange={e => {
                      const selectedId = e.target.value;
                      if (!selectedId) {
                        handleReturnSelect(null);
                      } else {
                        const selectedReturn = returns.find(r => r._id === selectedId);
                        if (selectedReturn) {
                          handleReturnSelect(selectedReturn);
                        }
                      }
                    }}
                    className="block w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loadingReturns}
                  >
                    <option value="">-- Select a return --</option>
                    {returns.map(returnItem => (
                      <option key={returnItem._id} value={returnItem._id}>
                        Return - {formatDate(returnItem.createdAt)}
                      </option>
                    ))}
                  </select>
                  {loadingReturns && (
                    <p className="text-xs text-gray-500 mt-1">Loading returns...</p>
                  )}
                  {returns.length === 0 && !loadingReturns && (
                    <p className="text-xs text-gray-500 mt-1">No returns found for this supplier</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credit Type</label>
                  <select
                    value={formData.credit_type}
                    onChange={e => handleInputChange('credit_type', e.target.value)}
                    className="block w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Return Items">Return Items</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Return Item Dated</label>
                  <input
                    type="date"
                    lang="en-IN"
                    value={formData.return_date}
                    onChange={e => handleInputChange('return_date', e.target.value)}
                    className="block w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => handleInputChange('description', e.target.value)}
                    rows="3"
                    placeholder="Enter description"
                    className="block w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={e => {
                      const value = e.target.value;
                      if (value === "") {
                        handleInputChange('amount', "");
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          handleInputChange('amount', value);
                        } else if (value === "0") {
                          handleInputChange('amount', "0");
                        }
                      }
                    }}
                    placeholder="Enter Amount"
                    className="block w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {error && (
                  <div className="text-red-500 text-sm bg-red-50 p-3 rounded-md break-words">
                    {error}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-4 border-t bg-white">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 text-yellow-600 border border-yellow-600 rounded-md bg-white hover:bg-yellow-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save Credit Note"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default SupplierAddCredit;